import { categories } from '@/mocks/categories';

export default function ValueSection() {
  const featuredCategories = categories.slice(0, 6);

  return (
    <section className="relative w-full py-24 md:py-32 px-6 md:px-14 bg-background-50">
      <div className="max-w-[1280px] mx-auto">
        <div className="flex flex-col lg:flex-row items-start gap-16 lg:gap-20">
          <div className="flex-1">
            <span className="inline-block px-4 py-1.5 rounded-full bg-accent-100 text-accent-700 text-xs font-medium tracking-wider uppercase mb-5">
              Why Choose Us
            </span>

            <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-foreground-950 leading-[1.12] mb-6">
              Why Use Our Pandabuy
              <br />
              Spreadsheet System?
            </h2>

            <p className="text-foreground-600 text-base md:text-lg leading-relaxed max-w-lg mb-4">
              Our <strong className="text-foreground-900">pandabuy spreadsheet system</strong> is designed to
              eliminate guesswork when sourcing products through shopping agents.
              We organize everything you need in one structured database.
            </p>
            <p className="text-foreground-500/70 text-sm leading-relaxed max-w-lg">
              Product quality ratings, price comparisons, shipping estimates, and
              QC reference data — all curated and verified every week.
            </p>

            <div className="grid grid-cols-2 gap-5 mt-10">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-accent-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="ri-check-double-line text-accent-600 text-lg"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-foreground-900 text-sm mb-1">Updated Weekly</h4>
                  <p className="text-foreground-500 text-xs leading-relaxed">Fresh spreadsheet data every week</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-accent-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="ri-shield-check-line text-accent-600 text-lg"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-foreground-900 text-sm mb-1">Verified Products</h4>
                  <p className="text-foreground-500 text-xs leading-relaxed">10,000+ entries checked for quality</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-accent-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="ri-price-tag-3-line text-accent-600 text-lg"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-foreground-900 text-sm mb-1">Price Compared</h4>
                  <p className="text-foreground-500 text-xs leading-relaxed">Find the best deals instantly</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-accent-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="ri-global-line text-accent-600 text-lg"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-foreground-900 text-sm mb-1">Global Access</h4>
                  <p className="text-foreground-500 text-xs leading-relaxed">Built for shoppers worldwide</p>
                </div>
              </div>
            </div>
          </div>

          <div className="flex-1 flex flex-col items-start gap-8 lg:pt-8">
            <div className="w-full max-w-md">
              <div
                className="w-full aspect-square rounded-2xl bg-cover bg-center"
                style={{
                  backgroundImage:
                    'url(https://readdy.ai/api/search-image?query=Close-up%20of%20premium%20white%20leather%20sneaker%20with%20colorful%20details%20placed%20on%20a%20stack%20of%20fashion%20magazines%20on%20a%20dark%20walnut%20wooden%20desk%2C%20warm%20tungsten%20desk%20lamp%20glow%20from%20above%2C%20creative%20studio%20atmosphere%2C%20moody%20editorial%20product%20photography%2C%20rich%20texture%20and%20contrast%2C%20no%20text%20no%20logos&width=700&height=700&seq=value-unique-02&orientation=squarish)',
                }}
              ></div>
            </div>

            <div className="flex items-baseline gap-2">
              <span className="font-heading text-8xl md:text-9xl font-bold text-foreground-950">10,000</span>
              <span className="text-foreground-500 text-lg">+</span>
            </div>
            <p className="text-foreground-600 text-base max-w-sm leading-relaxed">
              Verified product entries organized across 14 categories, with
              detailed quality ratings and pricing data updated every week.
            </p>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-7 py-3 rounded-full bg-foreground-950 text-background-50 font-semibold text-sm whitespace-nowrap hover:bg-foreground-800 transition-all duration-300 inline-flex items-center gap-2 group"
            >
              View Verified Items
              <i className="ri-arrow-right-up-line group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform"></i>
            </a>
          </div>
        </div>

        {/* Category Navigation Links with Images */}
        <div className="mt-20 md:mt-28">
          <div className="text-center mb-10">
            <span className="inline-block px-4 py-1.5 rounded-full bg-primary-100 text-primary-700 text-xs font-medium tracking-wider uppercase mb-3">
              Quick Browse
            </span>
            <h3 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
              Popular Pandabuy Spreadsheet Categories
            </h3>
            <p className="text-foreground-500 text-sm mt-2">
              Jump straight to your favorite category with one click
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {featuredCategories.map((cat) => (
              <a
                key={cat.slug}
                href={cat.link}
                target="_blank"
                rel="noopener nofollow"
                className="group relative rounded-xl overflow-hidden bg-background-100 aspect-[4/5] flex flex-col"
              >
                <div className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-110"
                  style={{ backgroundImage: `url(${cat.image})` }}
                ></div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
                <div className="relative mt-auto p-4">
                  <h4 className="font-semibold text-white text-sm mb-0.5">{cat.name}</h4>
                  <p className="text-white/70 text-xs line-clamp-1">{cat.description}</p>
                </div>
              </a>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}